import { API_URL } from "../../config";

/* ═══════════════════════════════════════════════════════════════════════════
   VENDOR PORTAL — API SERVICE
   Token-based auth, separate from the main admin RTK Query API
   ═══════════════════════════════════════════════════════════════════════════ */

const TOKEN_KEY = "vendor_token";
const VENDOR_KEY = "vendor_user";

export interface VendorUser {
  id: number;
  name: string;
  email: string;
  role: "admin" | "staff";
  company_name: string | null;
  phone: string | null;
  status: "active" | "inactive";
  must_change_password: boolean;
  vendor_id: number | null;
}

interface ApiResponse<T> {
  code: number;
  status: boolean;
  message: string;
  data: T;
}

/* ── Token helpers ──────────────────────────────────────────────────────── */

export const getVendorToken = (): string | null =>
  localStorage.getItem(TOKEN_KEY);

export const getStoredVendor = (): VendorUser | null => {
  const raw = localStorage.getItem(VENDOR_KEY);
  return raw ? JSON.parse(raw) : null;
};

const saveAuth = (token: string, vendor: VendorUser) => {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(VENDOR_KEY, JSON.stringify(vendor));
};

const clearAuth = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(VENDOR_KEY);
};

/* ── Fetch wrapper ──────────────────────────────────────────────────────── */

async function vendorFetch<T>(
  path: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const token = getVendorToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers as Record<string, string> || {}),
  };

  const res = await fetch(`${API_URL}${path}`, { ...options, headers });
  const data = await res.json();

  if (!res.ok) {
    throw { status: res.status, ...data };
  }

  return data;
}

/* ── Auth endpoints ─────────────────────────────────────────────────────── */

export async function vendorLogin(
  email: string,
  password: string
): Promise<{ token: string; vendor: VendorUser }> {
  const res = await vendorFetch<{ token: string; vendor: VendorUser }>(
    "/vendor/login",
    { method: "POST", body: JSON.stringify({ email, password }) }
  );
  saveAuth(res.data.token, res.data.vendor);
  return res.data;
}

export async function vendorGetMe(): Promise<VendorUser> {
  const res = await vendorFetch<VendorUser>("/vendor/me");
  localStorage.setItem(VENDOR_KEY, JSON.stringify(res.data));
  return res.data;
}

export async function vendorLogout(): Promise<void> {
  try {
    await vendorFetch("/vendor/logout", { method: "POST" });
  } finally {
    clearAuth();
  }
}

/* ── Event types ────────────────────────────────────────────────────────── */

export interface VendorEventListItem {
  id: number;
  booking_id: number;
  booking_ref: string;
  event_name: string;
  event_type: string;
  service_type: "food" | "bar" | "led" | "addon";
  status: "pending" | "confirmed" | "completed";
  event_date: string;
  event_time: string;
  guest_count: number;
  customer_name: string;
  customer_org: string | null;
  hall_name: string;
  notes: string | null;
}

export interface PricingItem {
  id: number;
  item_name: string;
  item_type: "vendor" | "global";
  base_price: number;
  vendor_price: number;
  adjustable: boolean;
  pricing_type: string;
  difference: number;
  diff_label: string;
  diff_type: "discount" | "increase" | "none";
}

export interface ServiceItem {
  label: string;
  value: string;
  price: number;
  type: string;
}

export interface HallItem {
  key: string;
  label: string;
  capacity: number;
}

export interface VendorEventDetail {
  id: number;
  booking_id: number;
  booking_ref: string;
  event_name: string;
  event_type: string;
  service_type: string;
  status: string;
  event_date: string;
  event_time: string;
  guest_count: number;
  children_count: number;
  customer: {
    name: string;
    organization: string | null;
    email: string | null;
    phone: string | null;
  } | null;
  halls: HallItem[];
  services: ServiceItem[];
  pricing: PricingItem[];
  subtotals: {
    food_subtotal: number;
    bar_subtotal: number;
    addons_subtotal: number;
    hall_subtotal: number;
  };
  notes: string | null;
}

/* ── Event endpoints ────────────────────────────────────────────────────── */

export async function vendorGetEvents(params?: {
  status?: string;
  search?: string;
}): Promise<VendorEventListItem[]> {
  const qs = new URLSearchParams();
  if (params?.status && params.status !== "all") qs.set("status", params.status);
  if (params?.search) qs.set("search", params.search);
  const query = qs.toString() ? `?${qs.toString()}` : "";
  const res = await vendorFetch<VendorEventListItem[]>(`/vendor/events${query}`);
  return res.data;
}

export async function vendorGetEvent(id: number): Promise<VendorEventDetail> {
  const res = await vendorFetch<VendorEventDetail>(`/vendor/events/${id}`);
  return res.data;
}

export async function vendorUpdatePricing(
  eventId: number,
  prices: { id: number; vendor_price: number }[]
): Promise<any> {
  const res = await vendorFetch(`/vendor/events/${eventId}/pricing`, {
    method: "PUT",
    body: JSON.stringify({ prices }),
  });
  return res.data;
}

/* ── Revenue types ──────────────────────────────────────────────────────── */

export interface RevenueTransaction {
  id: number;
  amount: number;
  status: string;
  description: string | null;
  booking_ref: string | null;
  event_name: string | null;
  date: string;
}

export interface RevenueData {
  total_revenue: number;
  monthly_revenue: number;
  upcoming_revenue: number;
  avg_booking_value: number;
  upcoming_events: number;
  chart: {
    labels: string[];
    values: number[];
  };
  recent_transactions: RevenueTransaction[];
}

export async function vendorGetRevenue(): Promise<RevenueData> {
  const res = await vendorFetch<RevenueData>("/vendor/revenue");
  return res.data;
}

/* ── Notification types ─────────────────────────────────────────────────── */

export interface VendorNotificationItem {
  id: number;
  type: string;
  title: string;
  message: string;
  meta: Record<string, any> | null;
  is_read: boolean;
  created_at: string;
  timestamp: string;
}

export interface NotificationsResponse {
  notifications: VendorNotificationItem[];
  unread_count: number;
}

export async function vendorGetNotifications(): Promise<NotificationsResponse> {
  const res = await vendorFetch<NotificationsResponse>("/vendor/notifications");
  return res.data;
}

export async function vendorMarkNotificationsRead(ids?: number[]): Promise<void> {
  await vendorFetch("/vendor/notifications/read", {
    method: "POST",
    body: JSON.stringify(ids ? { ids } : {}),
  });
}

/* ── Assignment types ─────────────────────────────────────────────────── */

export interface VendorAssignment {
  id: number;
  booking_id: number;
  booking_ref: string | null;
  event_name: string;
  event_type: string | null;
  event_date: string | null;
  event_time: string | null;
  guest_count: number;
  item_name: string;
  service_name: string | null;
  category_name: string | null;
  quantity: number;
  hours: number | null;
  rate: number;
  pricing_type: string;
  base_price: number;
  discount: number;
  final_price: number;
  notes: string | null;
}

export interface VendorAssignmentSummary {
  upcoming_count: number;
  total_assignments: number;
  total_revenue: number;
  upcoming_revenue: number;
  upcoming: {
    id: number;
    event_name: string;
    event_date: string | null;
    item_name: string;
    quantity: number;
    rate: number;
    final_price: number;
  }[];
}

export async function vendorGetAssignments(params?: {
  filter?: string;
  search?: string;
}): Promise<VendorAssignment[]> {
  const qs = new URLSearchParams();
  if (params?.filter) qs.set("filter", params.filter);
  if (params?.search) qs.set("search", params.search);
  const query = qs.toString() ? `?${qs.toString()}` : "";
  const res = await vendorFetch<VendorAssignment[]>(`/vendor/assignments${query}`);
  return res.data;
}

export async function vendorGetAssignmentSummary(): Promise<VendorAssignmentSummary> {
  const res = await vendorFetch<VendorAssignmentSummary>("/vendor/assignments/summary");
  return res.data;
}
